import * as Yup from 'yup';
import moment from 'moment';
import { MIN_VALID_PRICE, FIELD_REQUIRED } from 'utils/Constants';

export const RequiredRooms = Yup.object().shape({
  inputValue: Yup.number()
    .required(FIELD_REQUIRED)
    .min(1, 'Campo debe ser mayor o igual a 1')
});

const namesWithSpacesBetween = /^[a-zA-ZñÑáÁéÉíÍóÓúÚ_]+( [a-zA-ZñÑáÁéÉíÍóÓúÚ_]+)*$/;
const namesWithSpacesBetween3To40Length = Yup.string()
  .nullable()
  .matches(namesWithSpacesBetween, {
    message: 'Ingresa un valor con solo letras',
    excludeEmptyString: true
  })
  .transform((v, o) => (o === '' ? null : v))
  .min(3, 'Ingresa un valor de al menos 3 caracteres')
  .max(40, 'Ingresa un valor de maximo 40 caracteres');

export const RequiredNameSchema = Yup.object().shape({
  inputValue: namesWithSpacesBetween3To40Length.required(FIELD_REQUIRED)
});

export const RequiredMoneyMonthlyIncome = Yup.object().shape({
  inputValue: Yup.number()
    .typeError(FIELD_REQUIRED)
    .moreThan(5254, 'Campo debe ser mayor o igual a 5255')
});

export const NameSchema = Yup.object().shape({
  inputValue: namesWithSpacesBetween3To40Length
});

export const RequiredFieldSchema = Yup.object().shape({
  inputValue: Yup.string().required(FIELD_REQUIRED)
});

export const RequiredSqrMtsFieldSchema = Yup.object().shape({
  inputValue: Yup.string()
    .required(FIELD_REQUIRED)
    .notOneOf(['0'], FIELD_REQUIRED)
});

export const RequiredMoneyFieldSchema = Yup.object().shape({
  inputValue: Yup.number()
    .typeError(FIELD_REQUIRED)
    .moreThan(1, 'Campo debe ser mayor o igual a 1')
});

export const PhoneNumberWithCountryCodeSchema = Yup.object().shape({
  inputValue: Yup.string()
    .matches(
      /^[+]?[0-9]{8,23}$/,
      'Ingresa entre 8 a 23 dígitos, el signo + al inicio para el código de país (opcional)'
    )
    .required(FIELD_REQUIRED)
});

export const PhoneNumberSchema = Yup.object().shape({
  inputValue: Yup.string()
    .required(FIELD_REQUIRED)
    .matches(/^\d{8,20}$/, 'Ingresa entre 8 a 20 dígitos')
});

export const PhoneNumberSchemaApplication = Yup.object().shape({
  inputValue: Yup.string()
    .required(FIELD_REQUIRED)
    .matches(/^\d{8,10}$/, 'Ingresa entre 8 a 10 dígitos')
});

export const EmailSchema = Yup.object().shape({
  inputValue: Yup.string().email('Ingresa un correo electrónico valido')
});

export const RequiredEmailSchema = Yup.object().shape({
  inputValue: Yup.string()
    .email('Ingresa un correo electrónico valido')
    .required(FIELD_REQUIRED)
});

export const RequiredExtNumberSchema = Yup.object().shape({
  inputValue: Yup.string()
    .matches(/^[0-9]*$/, 'Ingresa solo numeros')
    .required(FIELD_REQUIRED)
});

export const IntNumberSchema = Yup.object().shape({
  inputValue: Yup.string().matches(/^[0-9]*$/, 'Ingresa solo numeros')
});

export const RequiredRFCSchema = Yup.object().shape({
  inputValue: Yup.string()
    .matches(
      /^([A-ZÑ&]{3,4}) ?(?:- ?)?(\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])) ?(?:- ?)?([A-Z\d]{2})([A\d])$/,
      {
        message: 'Ingresa un RFC valido',
        excludeEmptyString: true
      }
    )
    .required(FIELD_REQUIRED)
});

export const RFCSchema = Yup.object().shape({
  inputValue: Yup.string().matches(
    /^([A-ZÑ&]{3,4}) ?(?:- ?)?(\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])) ?(?:- ?)?([A-Z\d]{2})([A\d])$/,
    {
      message: 'Ingresa un RFC valido',
      excludeEmptyString: true
    }
  )
});

export const RequiredCurpSchema = Yup.object().shape({
  inputValue: Yup.string().matches(
    /^([A-Z][AEIOUX][A-Z]{2}\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])[HM](?:AS|B[CS]|C[CLMSH]|D[FG]|G[TR]|HG|JC|M[CNS]|N[ETL]|OC|PL|Q[TR]|S[PLR]|T[CSL]|VZ|YN|ZS)[B-DF-HJ-NP-TV-Z]{3}[A-Z\d])(\d)$/,
    {
      message: 'Ingresa un CURP valido',
      excludeEmptyString: true
    }
  )
});

const validationDateOfBirth = value => {
  const currentDate = moment();
  const dateBirth = moment(value, 'DD/MM/YYYY');
  return currentDate.diff(dateBirth, 'years') >= 18;
};

const transformToDate = (value, originalValue) => {
  value = moment(originalValue, 'DD/MM/YYYY', true);
  let result = originalValue === '' ? null : new Date('');
  return value.isValid() ? value.toDate() : result;
};
export const ageSchema = Yup.object().shape({
  inputValue: Yup.date()
    .typeError('Ingresa fecha válida (dd/mm/aaaa)')
    .transform(transformToDate)
    .min(new Date(1900, 0, 1), 'Ingresa una fecha mayor a 1900')
    .test('date', 'Debe ser mayor de edad', value => {
      return validationDateOfBirth(value);
    })
    .required(FIELD_REQUIRED)
});

export const RequiredDateSchema = Yup.object().shape({
  inputValue: Yup.date()
    .typeError('Ingresa fecha válida (dd/mm/aaaa)')
    .transform(transformToDate)
    .min(new Date(1900, 0, 1), 'Ingresa una fecha mayor a 1900')
    .required(FIELD_REQUIRED)
});

export const DateSchema = Yup.object().shape({
  inputValue: Yup.date()
    .nullable()
    .default(undefined)
    .typeError('Ingresa fecha válida (dd/mm/aaaa)')
    .transform(transformToDate)
    .min(new Date(1900, 0, 1), 'Ingresa una fecha mayor a 1900')
});

const filter = new RegExp(
  '^(0[123456789]|10|11|12)([/])([1-2][0-9][0-9][0-9])$'
);
export const CardValiditySchema = Yup.object().shape({
  inputValue: Yup.string()
    .matches(filter, {
      message: 'Ingresa vigencia válida (mm/aaaa)',
      excludeEmptyString: true
    })
    .required(FIELD_REQUIRED)
});

const filterYY = new RegExp('^(0[123456789]|10|11|12)([/])([1-9][0-9])$');
export const CardValidityYYSchema = Yup.object().shape({
  inputValue: Yup.string()
    .matches(filterYY, {
      message: 'Ingresa vigencia válida (mm/aa)',
      excludeEmptyString: true
    })
    .required(FIELD_REQUIRED)
});

const cvc = /^[0-9]{4}$/;
export const CVCSchema = Yup.object().shape({
  inputValue: Yup.string()
    .matches(cvc, {
      message: 'Ingresa cvc válido con al menos 3 dígitos',
      excludeEmptyString: true
    })
    .required(FIELD_REQUIRED)
});

const card = /^(?:4[0-9]{12}(?:[0-9]{3})?|[25][1-7][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/;
export const CARDSchema = Yup.object().shape({
  inputValue: Yup.string()
    .matches(card, {
      message: 'Ingresa número de tarjeta válido con 16 dígitos',
      excludeEmptyString: true
    })
    .required(FIELD_REQUIRED)
});

const cp = /^(?:0[1-9]|[1-9]\d|5[1-9])\d{3}$/;
export const CPSchema = Yup.object().shape({
  inputValue: Yup.string()
    .matches(cp, {
      message: 'Ingresa número de CP válido',
      excludeEmptyString: true
    })
    .required(FIELD_REQUIRED)
});

const cardFourNumber = /^\d{4}$/;
export const cardFourNumberSchema = Yup.object().shape({
  inputValue: Yup.string()
    .matches(cardFourNumber, {
      message: 'Ingresa 4 dígitos finales de tarjetas válidos',
      excludeEmptyString: true
    })
    .required(FIELD_REQUIRED)
});

export const isValidPrice = value => {
  return value >= MIN_VALID_PRICE;
};

export const PriceSchema = Yup.object().shape({
  inputValue: Yup.string()
    .test(
      'string',
      `El precio debe ser por lo menos de $${MIN_VALID_PRICE}`,
      value => {
        return isValidPrice(value);
      }
    )
    .required(FIELD_REQUIRED)
});

export const ValidateOnlyNumbers = Yup.object().shape({
  inputValue: Yup.number().typeError('Debes ingresar un número')
});

const TextAndNumber = /^[A-Za-zÁÉÍÓÚáéíóúñÑ0-9]+[A-Za-zÁÉÍÓÚáéíóúñÑ0-9\s]+[A-Za-zÁÉÍÓÚáéíóúñÑ0-9]+$/;

export const ValidateOnlyTextAndNumber = Yup.object().shape({
  inputValue: Yup.string()
    .matches(TextAndNumber, {
      message: 'Sólo números y letras'
    })
    .required(FIELD_REQUIRED)
});

export const ValidFileTypesOnIdentification =
  'image/jpg, image/jpeg, image/png';

export const ValidFileTypesOnDocuments =
  'image/jpg, image/jpeg, image/png, application/pdf';

export const isValidFileType = (file, validTypes) => {
  const validTypesToArr = validTypes.split(', ');
  return (
    validTypesToArr.includes(file.type) &&
    validTypesToArr
      .map(validType => validType.split('/')[1])
      .includes(file.name.split('.').pop())
  );
};

export const showAcceptFileTypes = validTypes => {
  const validTypesToArr = validTypes.split(', ');
  return validTypesToArr.map(validType => validType.split('/')[1]).join(', ');
};
